/**
 * Created by neo on 11.03.22.
 */
import { makeAutoObservable } from 'mobx';
import dayjs, { Dayjs } from 'dayjs';
import { TagCondition, createTagCondition } from '../Coach/SuperMacro/TagCondition';
import { LocalizedEntity, createLocalizedEntity } from '../LocalizedEntity';
import { Media, createMedia } from '../Media/Media';
import { Pageable } from '../Interfaces/Pageable';
import { LocalizedValue, createLocalizedValue } from '../LocalizedValue';
import { notUndefined } from '../../Utils/notUndefined';
import { ExploreEntryType } from './ExploreEntryType';

export type ExploreContentViewType = 'small_cards' | 'big_cards' | 'full_size';

export type ExploreContentQueryRequest = Pageable & {
  query?: string;
  type?: string[];
  categories?: string[];
  tags?: string[];
  gymId?: string;
  organizationId?: string;
  contentType?: string;
  root?: boolean;
};

export type ExploreContent = LocalizedEntity & {
  contentType: string;
  allParentIdentifiers: string[];
  type: ExploreEntryType;
  tags: string[];
  categories: string[];
  // V3 graph links (explore_*_v3): direct parent / child category UUIDs.
  parentIds: string[];
  childrenIds: string[];
  organizationId?: string;
  gymId?: string;
  order: number;
  images: Media[];
  videos: Media[];
  publishDate?: Dayjs;
  unpublishDate?: Dayjs;
  tagConditions: TagCondition[];
  viewType?: ExploreContentViewType;
  shortDescription: LocalizedValue[];
  language?: string;
  readonly isRoot: boolean;
  readonly availableLanguages: string[];
  getVideo(lang: string): Media | undefined;
  getImage(lang: string): Media | undefined;
};

export function createExploreContent(json?: Partial<ExploreContent>): ExploreContent {
  return makeAutoObservable({
    ...createLocalizedEntity(json),
    contentType: json?.contentType ?? 'entry',
    allParentIdentifiers: json?.allParentIdentifiers ?? [],
    type: json?.type ?? 'preview',
    tags: json?.tags ?? [],
    categories: json?.categories ?? [],
    parentIds: json?.parentIds ?? [],
    childrenIds: json?.childrenIds ?? [],
    organizationId: json?.organizationId,
    gymId: json?.gymId,
    order: json?.order ?? 0,
    images: (json?.images ?? []).map((m: any) => createMedia(m)),
    videos: (json?.videos ?? []).map((t: any) => createMedia(t)),
    tagConditions: (json?.tagConditions ?? []).map((t: any) => createTagCondition(t)),
    publishDate: json?.publishDate ? dayjs(json.publishDate as any) : undefined,
    unpublishDate: json?.unpublishDate ? dayjs(json.unpublishDate as any) : undefined,
    viewType: json?.viewType,
    shortDescription: (json?.shortDescription ?? []).map((s: any) => createLocalizedValue(s)),
    language: json?.language,

    get isRoot(): boolean {
      return this.categories.length === 0;
    },

    get availableLanguages(): string[] {
      return this.videos
        .flatMap((s) => s.language)
        .filter((v) => !!v)
        .filter(notUndefined);
    },

    getVideo(lang: string): Media | undefined {
      const languageVideo = this.videos.find((a) => a.language?.toLowerCase() === lang.toLowerCase());
      const germanVideo = this.videos.find((a) => a.language?.toLowerCase() === 'de');
      return languageVideo ?? germanVideo ?? this.videos[0];
    },

    getImage(lang: string): Media | undefined {
      const languageMedia = this.images.find((a) => a.language?.toLowerCase() === lang.toLowerCase());
      const germanMedia = this.images.find((a) => a.language?.toLowerCase() === 'de');
      return languageMedia ?? germanMedia ?? this.images[0];
    },
  });
}
